import { doc, getDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import type { GuestAccountRecord, Planet, PlayerPoints, PlayerPremium, PlayerSciences, ScienceId, TickCheckpointEvent } from '@kaeliux/shared';
import { DEFAULT_SCIENCES, normalizeEvent, parsePlayerPremium } from '@kaeliux/shared';
import { tickPlanet } from './planet.service';
import { normalizePlanet } from './planetNormalization';
import { getSelectedUniverseId } from '../store/universeStore';
import { authedPost } from './http';

const ZERO_POINTS: PlayerPoints = { resource: 0, battle: 0, total: 0 };

/**
 * Accepted (but ignored) by every real gameplay call, mirroring
 * `PlanetActionOptions` in `planet.service.ts` — mock mode consumes these
 * fields since it has no server-side universe doc to read from; the real
 * backend fetches its own. Kept so callers can pass one options object
 * regardless of which service implementation is aliased in.
 */
export interface ResearchActionOptions {
    resourceMultiplier?: number;
    sunAges?: Record<string, number[]>;
    speedMultiplier?: number;
}

/** Player-wide state returned alongside the planet by the science endpoints. */
export interface SciencePlayerState {
    sciences: PlayerSciences;
    tickCheckpoints: TickCheckpointEvent[];
    /**
     * Server-computed ranking points (`universes/{u}/players/{uid}.points`),
     * recalculated on every mutating request — see `commitAdvance` in
     * `apps/functions/src/advance.ts`. Read here rather than recomputed
     * client-side so points are always a straight read of the stored,
     * server-authoritative value.
     */
    points: PlayerPoints;
    /**
     * The planet as of the tick `getPlayerState` performs to resolve due
     * research. Set only by that getter — the mutation endpoints return the
     * planet at the top level of their own result instead.
     */
    planet?: Planet;
    /**
     * Player premium state (Dark Matter balance, tier + its expiry, unlocked
     * planet images) — read straight off `universes/{u}/players/{uid}.premium`
     * (issue #99), never from `useAuthStore().profile` (premium state must
     * never live on the client-writable `users/{uid}` doc, where a player
     * could self-grant it). The player's tier lives here as `premium.tier`;
     * `usePlayerStore` derives the effective tier from it.
     */
    premium?: PlayerPremium;
    /** Guest-account history (issue #332), written by the server on `players/{uid}`. */
    guestAccount?: GuestAccountRecord;
    /**
     * Whether the server has recorded a colonize flight this round
     * (`players/{uid}.colonizeFlightSentAt`). The tutorial's colonize step
     * counts it even after the flight turned back. Set only by `getPlayerState`.
     */
    colonizeFlightSent?: boolean;
}

export interface QueueScienceResult {
    planet: Planet;
    player: SciencePlayerState;
    /** Only present on `rushScienceResearch`'s response — the other callers of this shape don't debit Dark Matter. */
    premium?: PlayerPremium;
}

/**
 * The functions respond with `{ ...planetResponse(state), player:
 * playerResponse(state) }` — planet fields at the top level (same shape
 * `tickPlanet`/`upgradeBuilding` return) plus a nested `player` object.
 * `rushScienceResearch` additionally includes a top-level `premium`.
 */
function normalizeResult(raw: unknown, planetId: string): QueueScienceResult {
    const data = raw as Record<string, unknown>;
    const { player, premium, ...planetFields } = data;
    const playerData = (player ?? {}) as Record<string, unknown>;
    return {
        planet: normalizePlanet(planetFields, planetId),
        player: {
            sciences: playerData.sciences as PlayerSciences,
            tickCheckpoints: ((playerData.tickCheckpoints ?? []) as unknown[]).map(normalizeEvent),
            points: (playerData.points as PlayerPoints | undefined) ?? ZERO_POINTS,
        },
        ...(premium !== undefined ? { premium: parsePlayerPremium(premium) } : {}),
    };
}

export async function queueScience(
    planetId: string,
    scienceId: ScienceId,
    _options?: ResearchActionOptions,
): Promise<QueueScienceResult> {
    const data = await authedPost<unknown>('/queueScience', { planetId, scienceId });
    return normalizeResult(data, planetId);
}

export async function cancelScienceResearch(
    planetId: string,
    eventId: number,
    _options?: ResearchActionOptions,
): Promise<QueueScienceResult> {
    const data = await authedPost<unknown>('/cancelScienceResearch', { planetId, eventId });
    return normalizeResult(data, planetId);
}

/** Pay Dark Matter to instantly complete a pending scienceResearch event (never `gravitationalManipulators`). */
export async function rushScienceResearch(
    planetId: string,
    eventId: number,
    _options?: ResearchActionOptions,
): Promise<QueueScienceResult> {
    const data = await authedPost<unknown>('/rushScienceResearch', { planetId, eventId });
    return normalizeResult(data, planetId);
}

/**
 * Read current player state (sciences + pending research queue) from the
 * server-owned `universes/{u}/players/{uid}` doc — the only place completed
 * research levels and the queue actually live (writes are Admin-SDK-only,
 * via the Cloud Functions above; `firestore.rules` allows this read).
 * `profile.sciences` on the legacy `users/{uid}` doc is stale the moment a
 * real research completes — don't use it as a substitute for this.
 *
 * There is no dedicated "resolve the profile's due research" endpoint, so
 * this first calls `tickPlanet(planetId)` — the existing replay-only
 * endpoint already threads through `commitAdvance`, which resolves ANY due
 * `scienceResearch` event on the profile (not just the given planet's own
 * events) and bumps the science level in the same transaction. Without this
 * call, a completed-but-unresolved research would sit at "0:00" forever —
 * nothing else in the app passively re-triggers the server-side replay for
 * the profile's global queue.
 */
/** The player-wide fields of `universes/{u}/players/{uid}`, in the shape the client keeps them. */
export function normalizePlayerDoc(data: Record<string, unknown>): Omit<SciencePlayerState, 'planet'> {
    return {
        sciences: { ...DEFAULT_SCIENCES, ...(data.sciences as Partial<PlayerSciences> | undefined) },
        tickCheckpoints: ((data.tickCheckpoints ?? []) as unknown[]).map(normalizeEvent),
        points: (data.points as PlayerPoints | undefined) ?? ZERO_POINTS,
        premium: parsePlayerPremium(data.premium),
        guestAccount: {
            ...(data.startedAsGuest === true ? { startedAsGuest: true } : {}),
            ...(typeof data.guestUpgradedAt === 'number' ? { guestUpgradedAt: data.guestUpgradedAt } : {}),
        },
        colonizeFlightSent: data.colonizeFlightSentAt != null,
    };
}

export async function getPlayerState(uid: string, planetId: string): Promise<SciencePlayerState> {
    // Resolving due research is a side effect of this call, and completing a
    // science can change the planet itself — Physics multiplies energy
    // production, for one. Return the ticked planet rather than discarding it,
    // or callers render pre-research resources until the next heartbeat.
    const planet = await tickPlanet(planetId);

    const ref = doc(db, 'universes', getSelectedUniverseId(), 'players', uid);
    const snap = await getDoc(ref);
    if (!snap.exists()) {
        return { sciences: DEFAULT_SCIENCES, tickCheckpoints: [], points: ZERO_POINTS, premium: parsePlayerPremium(undefined), colonizeFlightSent: false, planet };
    }
    return { ...normalizePlayerDoc(snap.data() as Record<string, unknown>), planet };
}
